import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { StoryCard } from "@/components/StoryCard";
import { CharacterWall } from "@/components/CharacterWall";
import { FreeVsExtras } from "@/components/FreeVsExtras";
import { HolidayBanner } from "@/components/HolidayBanner";
import { IncomingMessageTeaser } from "@/components/IncomingMessageTeaser";
import { ArrowRight, Heart } from "lucide-react";

const GENRES = [
  "Dark Romance", "Mafia Romance", "Monster Romance", "Beastkin Romance",
  "Billionaire", "Enemies-to-Lovers", "Why Choose", "Slow Burn", "Age Gap",
  "Fantasy", "Sci-Fi", "LGBTQ+",
];

export default function Landing() {
  const [stories, setStories] = useState([]);
  const [trending, setTrending] = useState([]);
  const [contest, setContest] = useState(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) window.localStorage.setItem("mythos_ref", ref);
    } catch (e) { /* ignore */ }
    api.get("/stories?featured=true").then((r) => setStories(r.data)).catch(() => {});
    api.get("/stories/trending").then((r) => setTrending(r.data)).catch(() => {});
    api.get("/contests/active").then((r) => setContest(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      <IncomingMessageTeaser />
      <HolidayBanner />
      <CharacterWall />

      <div className="beam-divider max-w-6xl mx-auto" />
      <FreeVsExtras />
      <div className="beam-divider max-w-6xl mx-auto" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">Featured library · Interactive</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight">Full stories for after hours</h2>
          </div>
          <Link data-testid="see-all-stories" to="/library" className="hidden sm:inline-flex items-center gap-1 text-sm text-slate-400 hover:text-amber-300">See all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
          {stories.slice(0, 5).map((s) => <StoryCard key={s.story_id} story={s} />)}
        </div>
      </section>

      <section data-testid="landing-human-hearts" className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
        <Link to="/human-stories" className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl border border-rose-400/40 bg-rose-500/10 p-6 md:p-8 hover:shadow-[0_0_40px_-10px_rgba(147,51,255,0.8)] transition-shadow">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-rose-300 mb-1 flex items-center gap-2"><Heart className="h-3 w-3" /> Human Hearts</div>
            <div className="font-serif text-2xl md:text-3xl font-bold">Stories written by real people. Swipe through their profiles.</div>
          </div>
          <span className="inline-flex items-center gap-1 text-rose-200 group-hover:translate-x-1 transition-transform">Meet them <ArrowRight className="h-4 w-4" /></span>
        </Link>
      </section>

      <section data-testid="landing-genres" className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-rose-400 mb-2">Browse by genre</div>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold tracking-tight">Every reader's favorite — in one place.</h2>
          </div>
          <Link data-testid="landing-genres-cta" to="/library" className="hidden sm:inline-flex items-center gap-1 text-sm text-slate-400 hover:text-amber-300">All genres <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="flex flex-wrap gap-2">
          {GENRES.map((g) => (
            <Link
              key={g}
              to={`/library?genre=${encodeURIComponent(g)}`}
              data-testid={`landing-genre-${g.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-[#07080A] hover:border-amber-400/60 hover:text-amber-300 text-slate-300 h-10 px-4 text-xs font-mono uppercase tracking-widest transition-colors"
            >
              {g}
            </Link>
          ))}
        </div>
      </section>

      {trending.length > 0 && (
        <section data-testid="landing-trending" className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
          <div className="mb-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">Trending · tip-powered</div>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold tracking-tight">Most-tipped this week</h2>
            <p className="text-slate-400 text-sm mt-2 max-w-2xl">Readers tip the stories they love. Authors keep 70% of every gem.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {trending.slice(0, 6).map((s) => (
              <Link key={s.story_id} to={`/story/${s.story_id}`} data-testid={`trending-${s.story_id}`}
                    className="group rounded-xl overflow-hidden border border-white/10 bg-[#0F1117] hover:border-amber-400/40 transition-colors">
                <div className="aspect-[3/4] bg-black/50 overflow-hidden">
                  {s.cover_url && <img src={s.cover_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="p-2">
                  <div className="font-serif text-sm text-white truncate">{s.title}</div>
                  <div className="text-[10px] font-mono text-amber-300 mt-1">{s.tips_recent || 0} gems this week</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {contest && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          <div className="relative rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/8 via-transparent to-rose-500/8 p-8 md:p-14 overflow-hidden">
            <div className="relative">
              <div className="contest-pill inline-block mb-3">Live contest</div>
              <h2 className="font-serif text-3xl md:text-5xl font-bold leading-tight max-w-3xl">{contest.title}</h2>
              <p className="mt-4 text-slate-400 max-w-xl">{contest.description} Winner gets 1,000 gems.</p>
              <div className="mt-8">
                <Button asChild size="lg" className="rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-12 px-7">
                  <Link to="/contest">Enter the contest</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
